import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Phone } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { trackCall } from "@/lib/track";

const MaskLine = ({ children, delay }) => {
  const reduce = useReducedMotion();
  return (
    <span className="hero-line-mask">
      <motion.span
        initial={reduce ? false : { y: "110%" }}
        animate={reduce ? {} : { y: 0 }}
        transition={{ duration: 0.95, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
};

export const Hero = () => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 70]);

  return (
    <section className="hero" ref={ref} data-testid="hero-section">
      <div className="wrap hero-grid">
        <div>
          <motion.div
            className="hero-pill"
            initial={reduce ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            data-testid="hero-patent-pill"
          >
            Patent Pending · 19/642,047
          </motion.div>
          <h1>
            <MaskLine delay={0.1}>Build a metal fence.</MaskLine>
            <MaskLine delay={0.28}>
              <span className="em">No welder.</span>
            </MaskLine>
          </h1>
          <motion.p
            className="hero-warriors"
            initial={reduce ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.42, ease: "easeOut" }}
            data-testid="hero-warriors-line"
          >
            DIY NO WELD brackets are made for the weekend warriors
          </motion.p>
          <motion.p
            className="hero-sub"
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5, ease: "easeOut" }}
          >
            The DIY NO WELD Fence Bracket joins your rail to your post with <b>a drill and the screws in the box</b>. No welding machine to rent. No welding skill to learn. No welder to hire and wait on.
          </motion.p>
          <motion.p
            className="hero-offering"
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.58, ease: "easeOut" }}
            data-testid="hero-offering-line"
          >
            Offering brackets for metal fences, awnings and small buildings for the homeowner or welding challenged to complete projects on their own budget and time.
          </motion.p>
          <motion.div
            className="hero-cta"
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65, ease: "easeOut" }}
          >
            <a className="btn btn-primary" href="tel:+18666526569" onClick={() => trackCall("hero")} data-testid="hero-call-btn">
              <Phone size={17} aria-hidden="true" />
              Call to order — 866-652-6569
            </a>
            <button className="btn btn-ghost-dark" onClick={() => scrollToId("install")} data-testid="hero-install-btn">
              See how it installs
            </button>
          </motion.div>
          <motion.ul
            className="hero-kickers"
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.85 }}
          >
            <li>14 GA steel</li>
            <li>Round or square posts</li>
            <li>Tek screws included</li>
          </motion.ul>
        </div>

        <motion.div
          style={{ y }}
          initial={reduce ? false : { opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          <figure className="draw-card" style={{ margin: 0 }} data-testid="hero-drawing-card">
            <figcaption className="draw-card-head">
              <b>Part 200 — Bracket</b>
              <span>14 GA. Steel Plate</span>
            </figcaption>
            <img
              src="/assets/bracket.webp"
              alt="CAD line drawing of the Part 200 bracket: a flat steel strip with a cluster of post attachment holes at its center and an upturned rail attachment tab at each end"
              width="1310"
              height="619"
            />
          </figure>
        </motion.div>
      </div>
    </section>
  );
};
